import * as React from 'react'
import { Formik, Field, FieldProps } from 'formik'

import { LoginUser } from '@owl-nest/auth'
import { t } from '@owl-nest/localize'
import * as service from '@owl-nest/services'
import * as plume from '@ulule/owl-kit-components/next'
import * as env from '@owl-nest/config'

import { useAuthFailureMessage } from './useAuthFailureMessage'
import { apiErrorGetter, validateRequired } from './validators'
import { Identity } from './Identity'
import { ua } from '@owl-nest/utils'

import * as S from './styles'

type KnownUserProps = { auth: service.user.UseAuthKnown }

export function KnownUser({ auth }: KnownUserProps): React.ReactElement<KnownUserProps> {
  if (auth.knownUser.service === 'ulule') {
    return <UluleKnownUser auth={auth} />
  }

  return <ThirdPartyKnownUser auth={auth} service={auth.knownUser.service} />
}

function ThirdPartyKnownUser({
  auth,
  service,
}: { service: 'facebook' | 'google' } & KnownUserProps): React.ReactElement<unknown> {
  const isWebView = ua.isWebview(window.navigator.userAgent)

  return (
    <>
      <S.ThirdPartyAuthKnownUserForm noValidate onSubmit={(evt: React.FormEvent) => evt.preventDefault()}>
        <Identity />
        {(service === 'facebook' || !service) && (
          <plume.SocialButton
            platform="facebook"
            kind="round"
            type="button"
            onClick={() => {
              window.location.href = `${env.FACEBOOK_LOGIN_URL}?next=${window.location.href}`
            }}
          >
            {t('Log in with Facebook')}
          </plume.SocialButton>
        )}
        {!isWebView && (service === 'google' || !service) && (
          <plume.SocialButton
            platform="google"
            kind="round"
            type="button"
            onClick={() => {
              window.location.href = `${env.GOOGLE_LOGIN_URL}?next=${window.location.href}`
            }}
          >
            {t('Log in with Google')}
          </plume.SocialButton>
        )}
        {/* NOTE: We do not show the Google button on WebViews as it is not supported there. This is a known limitation to the Google OAuth2 service.
         **  Here, we target the extremely unlikely case where one would arrive within a WebView as a known user previously connected through Google,
         **  which would either mean that the user registered through the same WebView context a long time ago or that the main device browser shares
         **  the cookie context with the WebViews. To counter these edge cases, we show a fake "Log in" button to reset the state and avoid blocking the user.
         */}
        {isWebView && service === 'google' && (
          <plume.RoundButton fitToParent type="button" onClick={() => auth.forget()}>
            {t('Log in')}
          </plume.RoundButton>
        )}
      </S.ThirdPartyAuthKnownUserForm>
    </>
  )
}

type Form = LoginUser

function UluleKnownUser({ auth }: KnownUserProps): React.ReactElement<KnownUserProps> {
  useAuthFailureMessage(auth)

  const getApiErrors = apiErrorGetter<Form>(auth.failure)

  const originUrl = document !== undefined ? document.location.href : undefined

  return (
    <Formik<Form>
      initialValues={{ username: auth.knownUser.username, password: '', 'stay-connected': false }}
      onSubmit={auth.login}
    >
      {({ handleSubmit, validateField }) => {
        const apiError = getApiErrors({
          password: {
            OAuth2ServerError: t(
              'Please enter a correct username and password. Note that both fields are case-sensitive.',
            ),
          },
        })

        return (
          <S.UluleKnownUserForm noValidate onSubmit={handleSubmit}>
            <Identity />
            <Field name="password" validate={validateRequired}>
              {({ field, meta }: FieldProps<string>) => {
                React.useEffect(() => {
                  validateField(field.name)
                }, [field.value])
                return (
                  <plume.TextField
                    placeholder={t('Enter your password')}
                    label={t('Enter your password')}
                    type="password"
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={apiError('password') || (meta.touched ? meta.error : undefined)}
                  />
                )
              }}
            </Field>
            <S.KnownUserBlock>
              <Field name="stay-connected">
                {({ field }: FieldProps<boolean>) => {
                  return (
                    <plume.CheckboxField
                      label={t('Keep me logged in')}
                      name={field.name}
                      checked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )
                }}
              </Field>
              <plume.Link href={`${env.PASSWORD_RESET_URL}?origin_url=${originUrl}`}>
                {t('Forgot your password?')}
              </plume.Link>
            </S.KnownUserBlock>

            <plume.RoundButton fitToParent type="submit">
              {t('Login')}
            </plume.RoundButton>
          </S.UluleKnownUserForm>
        )
      }}
    </Formik>
  )
}
